import ensureErrorType from "../helpers/ensureErrorType";
import decodeJson from "../utils/Json";
import mergeObjects from "../utils/Merge";
import {HttpClientError} from './HttpClientError.js';
import {HttpRequest} from './HttpRequest.js';
import {HttpRequestOptions} from './HttpRequestOptions.js';
import {HttpRequestResult} from './HttpRequestResult.js';
import {ProblemDetails} from './ProblemDetails.js';
import {ProxyResponse} from "./ProxyResponse";

const _acceptHeader: string = "accept";
const _contentTypeHeader: string = "content-type";
const _jsonHeader: string = "application/json";
const _jsonPatchHeader: string = "application/json-patch+json";

type CacheEntry = {
	result: HttpRequestResult;
	timestamp: number;
	expiresAt: number;
};

function isInternalBodyType(body: any): boolean {
	if (body == null) {
		return false;
	}
	return typeof (body) === "string" || (body instanceof FormData) || (body instanceof ArrayBuffer) || (body instanceof URLSearchParams) || (body instanceof ReadableStream) || (body instanceof Blob);
}

function isProblemDetails(response: Response): boolean {
	let contentType = response.headers.get(_contentTypeHeader);
	return contentType?.includes("problem+json") === true || contentType?.includes("application/json") === true;
}

function methodHasBody(method: string): boolean {
	return method === "POST" || method === "PUT" || method === "PATCH";
}

function resultFromError(error: Error, request: RequestInit): HttpRequestResult {
	return {
		request: request,
		response: null!,
		error: HttpClient.errorFromError(error, null)
	};
}

async function resultToRequestResult(request: RequestInit, response: Response): Promise<HttpRequestResult> {
	if (response.ok) {
		return {
			request: request,
			response: response
		};
	}

	let text = await response.text();
	let problemDetails: ProblemDetails = {
		detail: text,
		status: response.status,
		title: response.statusText
	};

	if (isProblemDetails(response)) {
		problemDetails = decodeJson(text) || problemDetails;
	}

	return {
		request: request,
		response: response,
		error: new HttpClientError(problemDetails, request, response)
	};
}

function getRequestKey(url: string, method: string, body: any, options: HttpRequestOptions | null): string {
	return `${method}:${url}:${JSON.stringify(body)}:${JSON.stringify(options)}`;
}

class HttpClient {
	private readonly _baseUrl: string;
	private readonly _defaultOptions: HttpRequestOptions;
	private readonly _cache: Map<string, CacheEntry>;
	private readonly _pendingRequests: Map<string, Promise<HttpRequestResult>>;
	private readonly _pipelines: ((request: HttpRequest, next: (() => Promise<HttpRequestResult>)) => Promise<HttpRequestResult>)[];

	protected constructor(baseUrl: string, defaults: HttpRequestOptions | null) {
		this._baseUrl = baseUrl;
		this._pipelines = [];
		this._defaultOptions = defaults || {ttl: 0};
		this._cache = new Map();
		this._pendingRequests = new Map();
	}

	static errorFromError(error: Error, response: HttpRequestResult | null): HttpClientError {
		return new HttpClientError({
			status: 0,
			title: error.message,
			type: error.name
		}, response?.request ?? null, response?.response ?? null);
	}

	fetch(url: string, method: string, body: any | null, requestOptions: HttpRequestOptions | null): Promise<HttpRequestResult> {
		let options = mergeObjects(this._defaultOptions, requestOptions);
		const requestKey = getRequestKey(url, method, body, options);
		const now = Date.now();
		const ttl = options.ttl ?? 0;

		if (method === 'GET' && ttl > 0) {
			const cachedEntry = this._cache.get(requestKey);
			if (cachedEntry && cachedEntry.expiresAt > now) {
				return Promise.resolve(cachedEntry.result);
			}
		}

		if (this._pendingRequests.has(requestKey)) {
			return this._pendingRequests.get(requestKey)!;
		}

		url = this._getUrl(url, options);
		let requestHeaders = options.headers ??= {};
		let request: RequestInit = {
			cache: options.cache,
			credentials: options.credentials,
			headers: requestHeaders,
			method: method,
			mode: options.mode,
			signal: options.signal
		};

		let useJson = !isInternalBodyType(body);
		if (useJson) {
			requestHeaders[_acceptHeader] ??= _jsonHeader;
		}

		if (methodHasBody(method)) {
			if (useJson) {
				requestHeaders[_contentTypeHeader] ??= method === "PATCH" ? _jsonPatchHeader : _jsonHeader;
			}
			body = useJson ? body == null ? null : JSON.stringify(body) : body;
			if (body != null) {
				request.body = body;
			}
		}

		let result: () => Promise<HttpRequestResult> = async () => {
			try {
				return await resultToRequestResult(request, new ProxyResponse(await fetch(url, request)));
			} catch (__e) {
				return resultFromError(ensureErrorType(__e), request);
			}
		};

		let pipelines = this._pipelines;
		let requestData: HttpRequest = {
			body: body,
			headers: requestHeaders
		};

		for (let i = pipelines.length - 1; i >= 0; --i) {
			let copy = result;
			let pipeline = pipelines[i];
			result = () => pipeline(requestData, copy);
		}

		const requestPromise = result().then(result => {
			if (method === 'GET' && ttl > 0 && !result.error) {
				this._cache.set(requestKey, {
					result: result,
					timestamp: now,
					expiresAt: now + ttl
				});
			}

			return result;

		}).finally(() => {
			this._pendingRequests.delete(requestKey);
		});

		this._pendingRequests.set(requestKey, requestPromise);
		return requestPromise;
	}

	protected addPipeline(pipeline: ((request: HttpRequest, next: (() => Promise<HttpRequestResult>)) => Promise<HttpRequestResult>)): void {
		this._pipelines.push(pipeline);
	}

	protected isJson(response: Response): boolean {
		let contentType = response.headers.get(_contentTypeHeader);
		return contentType?.includes(_jsonHeader) === true;
	}

	private _getUrl(requestUrl: string, options: HttpRequestOptions): string {
		if (requestUrl.charAt(0) !== '/') {
			requestUrl = this._baseUrl + requestUrl;
		}
		let query = options.query;
		if (query == null) {
			return requestUrl;
		}
		requestUrl += requestUrl.includes("?") ? "&" : "?";
		for (let key of (Object.keys(query))) {
			const value = query[key];
			if (value != null) {
				requestUrl += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`;
			}
		}
		return requestUrl.substring(0, requestUrl.length - 1);
	}
}

export {HttpClient};
